// eslint-disable-next-line no-restricted-imports
import {
  LegalClientBeneficiaryFragment,
  Query_LegalClientBeneficiaryController_GetById_Status,
  Query_LegalClientBeneficiaryController_GetSnapshot_Value_Status,
  ClientUserFragment,
  Query_LegalClientBeneficiaryController_GetById_ControlOverLegalClientStatus,
  Query_LegalClientBeneficiaryController_GetSnapshot_Value_ControlOverLegalClientStatus,
} from 'api/generated/graphql';
import { LegalClientBeneficiaryEntity } from 'api/types/entity';
import { lodash } from 'helpers';

import { parseClientUserEntity } from '../client/parseEntity/parseClientUser';
import { parseFileDocumentEntity } from '../client/parseEntity/parseFileEntity';
import { parseTaxResidenceEntity } from '../client/parseEntity/parseTaxResidenceEntity';
import { parseCountryEntity } from '../parseCountryEntity';
import { parseIdentityDocumentWithFilesEntity } from '../parseEntity/parseIdentityDocumentEntity';
import { parseKycEntity } from '../parseEntity/parseKycEntity';
import {
  parsePepConnectionEntity,
  PepConnectionTypeApi,
} from '../parseEntity/parsePepConnectionEntity';
import { parseResidentialAddressEntity } from '../parseEntity/parseResidentialAddressEntity';
import { parseOboardingData } from '../parseOboardingData';
import { parsePlaceOfBirth } from '../parsePlaceOfBirth';

import { parseControlOverEntity } from './parseEntity';

export const parseLegalClientBeneficiaryEntity = (
  data: Omit<
    LegalClientBeneficiaryFragment,
    | '__typename'
    | 'pepConnectionType'
    | 'user'
    | 'status'
    | 'controlOverLegalClientStatus'
  > & {
    pepConnectionType?: PepConnectionTypeApi | null;
    status?:
      | Query_LegalClientBeneficiaryController_GetById_Status
      | Query_LegalClientBeneficiaryController_GetSnapshot_Value_Status
      | null;
    user?: ClientUserFragment;
    controlOverLegalClientStatus?:
      | Query_LegalClientBeneficiaryController_GetById_ControlOverLegalClientStatus
      | Query_LegalClientBeneficiaryController_GetSnapshot_Value_ControlOverLegalClientStatus
      | null;
  }
): LegalClientBeneficiaryEntity => ({
  id: data.id,
  versionNumber: data.version?.number,
  versionId: data.version?.id,
  createdAt: new Date(data.createdAt),
  deletedAt: data.deletedAt ? new Date(data.deletedAt) : undefined,
  dateOfBirth: new Date(data.dateOfBirth),
  firstName: data.firstName,
  lastName: data.lastName,
  middleName: data.middleName || undefined,
  legalClientId: data.legalClientId,
  onboarding: parseOboardingData(data.onboarding),
  email: data.email,
  isActive: data.user?.isActive,

  archived:
    data.status ===
    Query_LegalClientBeneficiaryController_GetById_Status.Archived,

  citizenships: data.citizenships
    ? lodash.compact(data.citizenships).map(parseCountryEntity)
    : undefined,
  taxResidences: data.taxResidences
    ? lodash.compact(data.taxResidences).map(parseTaxResidenceEntity)
    : undefined,
  placeOfBirth: data.placeOfBirth
    ? parsePlaceOfBirth(data.placeOfBirth)
    : undefined,
  registrationAddress: data.registrationAddress,
  residentialAddress: parseResidentialAddressEntity(data.residentialAddress),
  pepConnection: parsePepConnectionEntity(data),
  controlOver: parseControlOverEntity(data),
  additionalInformationFiles: lodash
    .compact(data.additionalInformationDocuments)
    .map(parseFileDocumentEntity),
  additionalDocumentFiles: lodash
    .compact(data.additionalDocuments)
    .map(parseFileDocumentEntity),

  kyc: parseKycEntity(data.kyc),

  selfieFiles: data.selfieDocuments
    ? lodash.compact(data.selfieDocuments).map(parseFileDocumentEntity)
    : undefined,
  identityDocument: data.identityDocument
    ? parseIdentityDocumentWithFilesEntity(data.identityDocument)
    : undefined,

  user: data.user ? parseClientUserEntity(data.user) : undefined,
});
